import { selectLocales } from "hooks/useAdapterLocale";
import i18next, { type TFunction } from "i18next";
import type { ICustomField } from "store/customFieldsStore";
import type { TPortalType } from "store/types";
import type { BankingFieldKey } from "types/countryFeatures";

import { templateExamples } from "./import_examples";
import type { TImportDataDictionary } from "./type";

// Reassigned by withLanguage to build labels in another language without switching the app language
let t: TFunction = i18next.t.bind(i18next) as TFunction;

const withLanguage = <T>(lng: string, build: () => T): T => {
    const previousT = t;
    t = i18next.getFixedT(lng);
    try {
        return build();
    } finally {
        t = previousT;
    }
};

/** FR and EN bundles of the active country variant (e.g. fr-GR / en-GR), falling back to the base language. */
const getMappingLanguages = () => {
    const country = i18next.language?.split("-")[1];
    return ["fr", "en"].map((base) => {
        const lng = country ? `${base}-${country}` : base;
        return i18next.hasResourceBundle(lng, "translation") ? lng : base;
    });
};

const mergeAcceptedKeys = (results: unknown[]) => {
    if (Array.isArray(results[0])) return Array.from(new Set(results.flat() as string[]));
    const merged: Record<string, string[]> = {};
    for (const result of results as Record<string, string[] | undefined>[]) {
        Object.entries(result).forEach(([key, values]) => {
            merged[key] = Array.from(new Set([...(merged[key] ?? []), ...(values ?? [])]));
        });
    }
    return merged;
};

export type TImportTitles = Record<string, string>;
export type TImportAcceptedKeys = Record<string, string[]>;
export type TImportImmaterielKeys = Record<string, string[]>;
export type TImportImmaterielTitles = Record<string, string | string[]>;

export type TImportConditions = {
    usesMultiPeriods?: boolean;
    usesCountryMarket?: boolean;
    usesOverridenPriceSales?: boolean;
    usesMultipleCurrencies?: boolean;
    hasBexioIntegration?: boolean;
    usesAdvanceCompensation?: boolean;
    usesTitleCompensation?: boolean;
    usesISSN?: boolean;
    usesFolio?: boolean;
    pressUsesBothChannels?: boolean;
    usesInitialAdvance?: boolean;
    usesMultipleBrands?: boolean;
    usesAlternativeMilestoneAmount?: boolean;
    /** Whether precount is set directly with selectable options (Belgium, Greece) */
    usesDirectPrecount?: boolean;
    choseAllChannels?: boolean;
    customFields?: ICustomField[];
    usesDistributorSalesData?: boolean;
    usesCumulativeRoyalties?: boolean;
    isProvisionAddedOnTitle?: boolean;
    allowsSubscriptionChannels?: boolean;
    /** Banking fields for the company's country (from countryFeatures.bankingFields) */
    bankingFields?: BankingFieldKey[];
    /** Whether the company uses URSSAF (false for Canada) */
    urssaf?: boolean;
    /** Whether the company has VAT options (false for Canada) */
    hasVat?: boolean;
    /** Whether the company has international banking enabled */
    internationalBankingEnabled?: boolean;
    /** Whether the selected channel (or one of the channels in all-channels mode) is a collective management channel */
    isCollectiveManagementChannel?: boolean;
    /**
     * Company labels for calculation-base columns (settings). `turnover` / `quantity` rename the
     * matching import column; `turnover_1` / `turnover_2` also gate whether those columns appear.
     */
    customTurnoverLabels?: {
        quantity?: string;
        turnover?: string;
        turnover_1?: string;
        turnover_2?: string;
    };
    /** Bulk update only: keep legacy channel ISBN / name / releaseDate columns. */
    isBulkUpdate?: boolean;
};

const defaultCalculationBaseLabel = (column: "quantity" | "turnover") =>
    t(`CalculationBaseColumnLabels.Columns.${column}`);

const resolveCalculationBaseLabel = (
    column: "quantity" | "turnover",
    custom?: TImportConditions["customTurnoverLabels"],
) => custom?.[column] || defaultCalculationBaseLabel(column);

const calculationBaseAcceptedKeys = (
    column: "quantity" | "turnover",
    custom?: TImportConditions["customTurnoverLabels"],
) => [
    ...new Set([column, defaultCalculationBaseLabel(column), custom?.[column]].filter((key): key is string => !!key)),
];

type TKeyData = {
    label: string;
    acceptedKeys: string[];
    templateExamples?: string;
    valueType: string[];
    immaterielKey?: string[];
    immaterielTitle?: string | string[];
    floatKey?: boolean;
    repeatedKey?: boolean;
    mandatory?: boolean;
};

export type TImportData = {
    keys: Record<string, TKeyData>;
    relatedDoc?: string;
};

/** CSV export headers (FR + EN). Keep oe without ligature so older files still auto-map. */
const EXPORT_WORK_HEADERS = {
    workId: ["identifiant de l'œuvre", "identifiant de l'oeuvre", "work ID"],
    workName: ["œuvre", "oeuvre", "work"],
    workCode: ["code œuvre", "code oeuvre", "work code"],
    artworkNature: ["nature de l'œuvre", "nature de l'oeuvre", "artwork nature"],
    analysisCode: ["code analytique", "analytic code"],
    associatedBrand: ["marque associée", "associated brand"],
    compensationGroup: ["groupe de compensation", "compensation group"],
} as const;

const EXPORT_FORMAT_TYPE_HEADERS = {
    formatTypeName: ["type de format", "format type"],
    formatTypeId: ["identifiant du type de format", "format type ID"],
} as const;

const EXPORT_COVERED_TITLE_HEADERS = ["identifiants des articles concernés", "covered article IDs"] as const;

/** Legacy "Titre" / "Title" / "Format" headers still map to the article name column. */
const EXPORT_TITLE_NAME_HEADERS = ["titre", "Titre", "title", "Title", "format", "Format"] as const;

const getNameTranslationKeys = () => {
    const locales = selectLocales();
    const keys = {};
    for (const locale of locales) {
        keys[`name_${locale.code}`] = {
            label: t(`ImportData.TitleName_${locale.code}`),
            acceptedKeys: [
                `name_${locale.code}`,
                t(`ImportData.TitleName_${locale.code}`),
                `Titre ${locale.code === "fr" ? "français" : locale.code === "en" ? "anglais" : locale.code}`,
                `Title ${locale.code}`,
            ],
            templateExamples: templateExamples.name,
            valueType: ["string"],
        };
    }
    return keys;
};

const insertInternationalBankingColumns = (keys: Record<string, TKeyData>): Record<string, TKeyData> => {
    const intlFields: [string, string][] = [
        ["bank_account_country", t("ImportData.BenBankAccountCountry")],
        ["intl_swift", t("ImportData.BenIntlSwift")],
        ["intl_account_number", t("ImportData.BenIntlAccountNumber")],
        ["intl_routing_code", t("ImportData.BenIntlRoutingCode")],
        ["intl_bank_name", t("ImportData.BenIntlBankName")],
    ];
    const newKeys: Record<string, TKeyData> = {};
    for (const [k, v] of Object.entries(keys)) {
        newKeys[k] = v;
        if (k === "ribOwnerName") {
            for (const [fieldKey, label] of intlFields) {
                newKeys[fieldKey] = {
                    label,
                    acceptedKeys: [fieldKey, label],
                    templateExamples: (templateExamples as Record<string, string>)[fieldKey] ?? "",
                    valueType: ["string"],
                };
            }
        }
    }
    return newKeys;
};

/** Converts a snake_case banking field key to its ImportData i18n key, e.g. "institution_number" → "ImportData.BenInstitutionNumber" */
const toBankingFieldI18nKey = (fieldKey: string) =>
    `ImportData.Ben${fieldKey
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join("")}`;

export const getBasicImportTypeData = <T extends TImportDataDictionary>(
    type: TImportDataDictionary,
    publisherType: TPortalType | undefined,
    conditions?: TImportConditions,
): TImportData => {
    const data = {
        sales: {
            keys: {
                ean13: {
                    label: t("Management.ImportEAN13"),
                    acceptedKeys: ["ean13", t("Management.ImportEAN13")],
                    templateExamples: templateExamples.ean13,
                    immaterielKey: ["EAN offre numérique"],
                    immaterielLabel: "EAN offre numérique",
                    valueType: ["string"],
                    mandatory: true,
                },
                /**
                 * Calculation-base columns of the line, each under its own name. A sale can fill both
                 * at once — copies and turnover — and every contract reads the column it computes on,
                 * so nothing depends on how the channel was configured.
                 */
                quantity: {
                    label: resolveCalculationBaseLabel("quantity", conditions?.customTurnoverLabels),
                    acceptedKeys: calculationBaseAcceptedKeys("quantity", conditions?.customTurnoverLabels),
                    templateExamples: templateExamples.quantity,
                    immaterielKey: ["Quantité "],
                    immaterielLabel: "Quantité ",
                    valueType: ["number", "string", "null"],
                },
                turnover: {
                    label: resolveCalculationBaseLabel("turnover", conditions?.customTurnoverLabels),
                    acceptedKeys: calculationBaseAcceptedKeys("turnover", conditions?.customTurnoverLabels),
                    templateExamples: templateExamples.turnover,
                    immaterielKey: ["CA (€) "],
                    immaterielLabel: "CA (€) ",
                    valueType: ["number", "string", "null"],
                    floatKey: true,
                },
                note: {
                    label: t("Management.ImportNote"),
                    acceptedKeys: ["note", t("Management.ImportNote")],
                    templateExamples: templateExamples.note,
                    immaterielKey: [],
                    immaterielLabel: t("Management.ImportNote"),
                    valueType: ["string"],
                },
                returnedSales: {
                    label: t("Management.ReturnsQuantities"),
                    acceptedKeys: ["returnedSales", t("Management.ReturnsQuantities")],
                    templateExamples: templateExamples.returnedSales,
                    immaterielKey: [],
                    immaterielLabel: t("Management.ReturnsQuantities"),
                    valueType: ["string"],
                },
                source: {
                    label: t("Management.Source"),
                    acceptedKeys: ["source", t("Management.Source")],
                    templateExamples: templateExamples.source,
                    immaterielKey: ["Revendeur"],
                    immaterielLabel: "Revendeur",
                    valueType: ["string"],
                },
                date: {
                    label: t("Management.SaleDate"),
                    acceptedKeys: ["date", t("Management.SaleDate")],
                    templateExamples: templateExamples.saleDate,
                    immaterielKey: ["Date de mise en vente"],
                    immaterielLabel: t("Management.SaleDate"),
                    valueType: ["string", "undefined"],
                },
            },
            relatedDoc: "https://helpdesk.crealo.app/fr/article/importer-des-ventes-1huz39s/?bust=1697813385419",
        },
        titles: {
            keys: {
                name: {
                    label: t("ImportData.TitleName"),
                    acceptedKeys: ["name", t("ImportData.TitleName"), ...EXPORT_TITLE_NAME_HEADERS],
                    templateExamples: templateExamples.name,
                    valueType: ["string"],
                    mandatory: true,
                },
                workCode: {
                    label: t("ImportData.WorkCode"),
                    acceptedKeys: [
                        "workCode",
                        "work_code",
                        t("ImportData.WorkCode"),
                        t("Works.WorkCode"),
                        ...EXPORT_WORK_HEADERS.workCode,
                    ],
                    templateExamples: templateExamples.workCode,
                    valueType: ["string"],
                },
                workName: {
                    label: t("ImportData.WorkName"),
                    acceptedKeys: [
                        "workName",
                        "work_name",
                        t("ImportData.WorkName"),
                        t("Works.WorkName"),
                        ...EXPORT_WORK_HEADERS.workName,
                    ],
                    templateExamples: templateExamples.workName,
                    valueType: ["string"],
                },
                workId: {
                    label: t("ImportData.WorkId"),
                    acceptedKeys: ["workId", "work_id", t("ImportData.WorkId"), ...EXPORT_WORK_HEADERS.workId],
                    templateExamples: templateExamples.workId,
                    valueType: ["string", "number"],
                },
                releaseDate: {
                    label: t("ImportData.TitleReleaseDate"),
                    acceptedKeys: ["releaseDate", t("ImportData.TitleReleaseDate")],
                    templateExamples: templateExamples.releaseDate,
                    valueType: ["string"],
                },
                ean13: {
                    label: t("ImportData.EAN13"),
                    acceptedKeys: ["ean13", t("ImportData.EAN13")],
                    templateExamples: templateExamples.ean13,
                    valueType: ["string"],
                    mandatory: true,
                },
                sku: {
                    label: t("Titles.SKU"),
                    acceptedKeys: ["sku", t("Titles.SKU")],
                    templateExamples: templateExamples.sku,
                    valueType: ["string"],
                },
                price: {
                    label: t("ImportData.TitlePrice"),
                    acceptedKeys: ["price", t("ImportData.TitlePrice")],
                    templateExamples: templateExamples.price,
                    valueType: ["string", "number"],
                    floatKey: true,
                    mandatory: true,
                },
                distributorReference: {
                    label: t("Titles.DistributorReference"),
                    acceptedKeys: ["distributorReference", t("Titles.DistributorReference")],
                    templateExamples: "",
                    valueType: ["string"],
                },
                formatType: {
                    label: t("Titles.FormatType"),
                    acceptedKeys: [
                        "formatType",
                        "formatTypeName",
                        "format_type",
                        "format_type_name",
                        t("Titles.FormatType"),
                        ...EXPORT_FORMAT_TYPE_HEADERS.formatTypeName,
                    ],
                    templateExamples: templateExamples.formatType,
                    valueType: ["string"],
                },
                formatTypeId: {
                    label: t("ImportData.FormatTypeId"),
                    acceptedKeys: [
                        "formatTypeId",
                        "format_type_id",
                        t("ImportData.FormatTypeId"),
                        ...EXPORT_FORMAT_TYPE_HEADERS.formatTypeId,
                    ],
                    templateExamples: templateExamples.formatTypeId,
                    valueType: ["string", "number"],
                },
                format: {
                    label: t("ImportData.TitleFormat"),
                    acceptedKeys: ["format", t("ImportData.TitleFormat"), t("NewTitle.FormatInformation")],
                    templateExamples: "",
                    valueType: ["string"],
                },
                pagination: {
                    label: t("ImportData.TitlePagination"),
                    acceptedKeys: ["pagination", t("ImportData.TitlePagination")],
                    templateExamples: "",
                    valueType: ["string"],
                },
                draw: {
                    label: t("ImportData.TitleDraw"),
                    acceptedKeys: ["draw", t("ImportData.TitleDraw")],
                    templateExamples: "",
                    valueType: ["string"],
                },
                prize: {
                    label: t("ImportData.TitlePrize"),
                    acceptedKeys: ["prize", t("ImportData.TitlePrize")],
                    templateExamples: "",
                    valueType: ["string"],
                },
                summary: {
                    label: t("ImportData.TitleSummary"),
                    acceptedKeys: ["summary", t("ImportData.TitleSummary")],
                    templateExamples: templateExamples.summary,
                    valueType: ["string"],
                },
                category: {
                    label: t("Titles.Category"),
                    acceptedKeys: ["category", t("Titles.Category")],
                    templateExamples: "",
                    valueType: ["string"],
                },
                initialInventory: {
                    label: t("ImportData.TitleChannelQuantityStock"),
                    acceptedKeys: ["initialInventory", t("ImportData.TitleChannelQuantityStock")],
                    templateExamples: templateExamples.inventory,
                    valueType: ["string"],
                },
                analysisCode: {
                    label: t("ImportData.TitleAnalysisCode"),
                    acceptedKeys: ["analysisCode", t("ImportData.TitleAnalysisCode")],
                    templateExamples: templateExamples.analysisCode,
                    valueType: ["string"],
                },
                channelId: {
                    label: t("ImportData.ChannelId"),
                    acceptedKeys: ["channelId", t("ImportData.ChannelId")],
                    templateExamples: templateExamples.channelId,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                channelISBN: {
                    label: t("ImportData.ChannelISBN"),
                    acceptedKeys: ["channelISBN", t("ImportData.ChannelISBN")],
                    templateExamples: templateExamples.channelISBN,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                channelBookTitle: {
                    label: t("ImportData.ChannelBookTitle"),
                    acceptedKeys: ["channelBookTitle", t("ImportData.ChannelBookTitle")],
                    templateExamples: templateExamples.channelBookTitle,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                channelReleaseDate: {
                    label: t("ImportData.ChannelReleaseDate"),
                    acceptedKeys: ["channelReleaseDate", t("ImportData.ChannelReleaseDate")],
                    templateExamples: templateExamples.channelReleaseDate,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                quantity: {
                    label: resolveCalculationBaseLabel("quantity", conditions?.customTurnoverLabels),
                    acceptedKeys: calculationBaseAcceptedKeys("quantity", conditions?.customTurnoverLabels),
                    templateExamples: templateExamples.quantity,
                    valueType: ["string", "array", "number"],
                    repeatedKey: true,
                },
                turnover: {
                    label: resolveCalculationBaseLabel("turnover", conditions?.customTurnoverLabels),
                    acceptedKeys: calculationBaseAcceptedKeys("turnover", conditions?.customTurnoverLabels),
                    templateExamples: templateExamples.turnover,
                    valueType: ["string", "array", "number"],
                    floatKey: true,
                    repeatedKey: true,
                },
                channelPrice: {
                    label: t("ImportData.TitleChannelPrice"),
                    acceptedKeys: ["channelPrice", t("ImportData.TitleChannelPrice")],
                    templateExamples: templateExamples.channelPrice,
                    valueType: ["string", "array", "null", "number"], // Object for null
                    floatKey: true,
                    repeatedKey: true,
                },
                tags: {
                    label: t("ImportData.TitleTag"),
                    acceptedKeys: ["tags", t("ImportData.TitleTag")],
                    templateExamples: templateExamples.tags,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                ...getNameTranslationKeys(),
            },
            relatedDoc: "https://helpdesk.crealo.app/fr/article/importer-des-titres-etbyuh/",
        },
        beneficiars: {
            keys: {
                beneficiaryCode: {
                    label: t("ImportData.BenCode"),
                    acceptedKeys: ["beneficiaryCode", t("ImportData.BenCode")],
                    templateExamples: templateExamples.beneficiaryCode,
                    valueType: ["string"],
                },
                entity: {
                    label: t("ImportData.BenEntity"),
                    acceptedKeys: ["entity", t("ImportData.BenEntity")],
                    templateExamples: templateExamples.entity,
                    valueType: ["string"],
                    mandatory: true,
                },
                name: {
                    label: t("ImportData.BenName"),
                    acceptedKeys: ["name", t("ImportData.BenName")],
                    templateExamples: templateExamples.beneficiaryName,
                    valueType: ["string"],
                    mandatory: true,
                },
                surname: {
                    label: t("ImportData.BenSurname"),
                    acceptedKeys: ["surname", t("ImportData.BenSurname")],
                    templateExamples: templateExamples.beneficiarySurname,
                    valueType: ["string"],
                    mandatory: true,
                },
                pseudonym: {
                    label: t("ImportData.BenPseudonym"),
                    acceptedKeys: ["pseudonym", t("ImportData.BenPseudonym")],
                    templateExamples: templateExamples.pseudonym,
                    valueType: ["string"],
                },
                street: {
                    label: t("ImportData.BenStreet"),
                    acceptedKeys: ["street", t("ImportData.BenStreet")],
                    templateExamples: templateExamples.street,
                    valueType: ["string"],
                },
                zipCode: {
                    label: t("ImportData.BenZipCode"),
                    acceptedKeys: ["zipCode", t("ImportData.BenZipCode")],
                    templateExamples: templateExamples.zipCode,
                    valueType: ["string"],
                },
                city: {
                    label: t("ImportData.BenCity"),
                    acceptedKeys: ["city", t("ImportData.BenCity")],
                    templateExamples: templateExamples.city,
                    valueType: ["string"],
                },
                country: {
                    label: t("ImportData.BenCountry"),
                    acceptedKeys: ["country", t("ImportData.BenCountry")],
                    templateExamples: templateExamples.country,
                    valueType: ["string"],
                },
                phoneNumber: {
                    label: t("ImportData.BenPhoneNumber"),
                    acceptedKeys: ["phoneNumber", t("ImportData.BenPhoneNumber")],
                    templateExamples: templateExamples.phoneNumber,
                    valueType: ["string"],
                },
                birthDate: {
                    label: t("ImportData.BenBirtDate"),
                    acceptedKeys: ["birthDate", t("ImportData.BenBirtDate")],
                    templateExamples: templateExamples.birthDate,
                    valueType: ["string"],
                },
                activityType: {
                    label: t("ImportData.BenActivityType"),
                    acceptedKeys: ["activityType", t("ImportData.BenActivityType")],
                    templateExamples: templateExamples.activityType,
                    valueType: ["string"],
                },
                email: {
                    label: t("ImportData.BenEmail"),
                    acceptedKeys: ["email", t("ImportData.BenEmail")],
                    templateExamples: templateExamples.email,
                    valueType: ["string"],
                },
                siretNumber: {
                    label: t("ImportData.BenSiretNumber"),
                    acceptedKeys: ["siretNumber", t("ImportData.BenSiretNumber")],
                    templateExamples: templateExamples.siretNumber,
                    valueType: ["string"],
                },
                socialSecurityNumber: {
                    label: t("ImportData.BenSocialSecurityNumber"),
                    acceptedKeys: ["socialSecurityNumber", t("ImportData.BenSocialSecurityNumber")],
                    templateExamples: templateExamples.socialSecurityNumber,
                    valueType: ["string"],
                },
                socialProfileType: {
                    label: t("ImportData.BenSocialProfileType"),
                    acceptedKeys: ["socialProfileType", t("ImportData.BenSocialProfileType")],
                    templateExamples: templateExamples.socialProfileType,
                    valueType: ["string"],
                },
                vat: {
                    label: t("ImportData.BenVat"),
                    acceptedKeys: ["vat", t("ImportData.BenVat")],
                    templateExamples: t("ImportDataTemplateExamples.vat"),
                    valueType: ["string"],
                },
                iban: {
                    label: t("ImportData.BenBankTransfer"),
                    acceptedKeys: ["iban", t("ImportData.BenBankTransfer")],
                    templateExamples: templateExamples.iban,
                    valueType: ["string"],
                },
                swift: {
                    label: t("ImportData.BenSwift"),
                    acceptedKeys: ["swift", t("ImportData.BenSwift")],
                    templateExamples: templateExamples.swift,
                    valueType: ["string"],
                },
                ribOwnerName: {
                    label: t("ImportData.BenRibOwnerName"),
                    acceptedKeys: ["ribOwnerName", t("ImportData.BenRibOwnerName")],
                    templateExamples: templateExamples.ribOwnerName,
                    valueType: ["string"],
                },
                paypalEmail: {
                    label: t("ImportData.BenPaypal"),
                    acceptedKeys: ["paypalEmail", t("ImportData.BenPaypal")],
                    templateExamples: templateExamples.paypalEmail,
                    valueType: ["string"],
                },
                bankCheck: {
                    label: t("ImportData.BenCheck"),
                    acceptedKeys: ["bankCheck", t("ImportData.BenCheck")],
                    templateExamples: templateExamples.bankCheck,
                    valueType: ["string"],
                },
                accountingTypology: {
                    label: t("ImportData.BenAccountingTypology"),
                    acceptedKeys: ["accountingTypology", t("ImportData.BenAccountingTypology")],
                    templateExamples: templateExamples.accountingTypology,
                    valueType: ["string"],
                },
                accountingCode: {
                    label: t("ImportData.BenAccountingCode"),
                    acceptedKeys: ["accountingCode", t("ImportData.BenAccountingCode")],
                    templateExamples: templateExamples.accountingCode,
                    valueType: ["string"],
                },
                tags: {
                    label: t("ImportData.TitleTag"),
                    acceptedKeys: ["tags", t("ImportData.TitleTag")],
                    templateExamples: templateExamples.tags,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
            },
            relatedDoc: "https://helpdesk.crealo.app/fr/article/importer-des-beneficiaires-181ew79/",
        },
        publications: {
            keys: {
                name: {
                    label: t(`ImportData.PublicationName_${publisherType}`),
                    acceptedKeys: [
                        "name",
                        t(`ImportData.PublicationName_${publisherType}`),
                        t("ImportData.TitleName"),
                        ...EXPORT_TITLE_NAME_HEADERS,
                    ],
                    templateExamples: templateExamples.name,
                    valueType: ["string"],
                    mandatory: true,
                },
                releaseDate: {
                    label: t("ImportData.TitleReleaseDate"),
                    acceptedKeys: ["releaseDate", t("ImportData.TitleReleaseDate")],
                    templateExamples: templateExamples.releaseDate,
                    valueType: ["string"],
                },
                deliveryDate: {
                    label: t("ImportData.TitleDeliveryDate"),
                    acceptedKeys: ["deliveryDate", t("ImportData.TitleDeliveryDate")],
                    templateExamples: templateExamples.deliveryDate,
                    valueType: ["string"],
                },
                analysisCode: {
                    label: t("ImportData.TitleAnalysisCode"),
                    acceptedKeys: ["analysisCode", t("ImportData.TitleAnalysisCode")],
                    templateExamples: templateExamples.analysisCode,
                    valueType: ["string"],
                },
                artworkNature: {
                    label: t("ImportData.ArtworkNature"),
                    acceptedKeys: ["artworkNature", t("ImportData.ArtworkNature")],
                    templateExamples: templateExamples.artworkNature,
                    valueType: ["string"],
                },
                tags: {
                    label: t("ImportData.TitleTag"),
                    acceptedKeys: ["tags", t("ImportData.TitleTag")],
                    templateExamples: templateExamples.tags,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
            },
            relatedDoc: "https://presse-helpdesk.crealo.app/fr/article/importer-des-parutions-eurqio/",
        },
        publicationsWithArticles: {
            keys: {
                issueName: {
                    label: t(`ImportData.PublicationName_${publisherType}`),
                    acceptedKeys: ["issueName", t(`ImportData.PublicationName_${publisherType}`)],
                    templateExamples: templateExamples.issueName,
                    valueType: ["string"],
                    mandatory: true,
                },
                issueAnalysisCode: {
                    label: t("ImportData.TitleAnalysisCode"),
                    acceptedKeys: ["analysisCode", t("ImportData.TitleAnalysisCode")],
                    templateExamples: templateExamples.analysisCode,
                    valueType: ["string"],
                },
                issueReleaseDate: {
                    label: t("ImportData.TitleReleaseDate"),
                    acceptedKeys: ["issueReleaseDate", t("ImportData.TitleReleaseDate")],
                    templateExamples: templateExamples.releaseDate,
                    valueType: ["string"],
                },
                issueDeliveryDate: {
                    label: t("ImportData.TitleDeliveryDate"),
                    acceptedKeys: ["issueDeliveryDate", t("ImportData.TitleDeliveryDate")],
                    templateExamples: templateExamples.deliveryDate,
                    valueType: ["string"],
                },
                articleName: {
                    label: t(`ImportData.ArticleName_${publisherType}`),
                    acceptedKeys: ["articleName", t(`ImportData.ArticleName_${publisherType}`)],
                    templateExamples: templateExamples.articleName,
                    valueType: ["string"],
                    mandatory: true,
                },
                articleBeneficiaryId: {
                    label: t("ImportData.BeneficiarId"),
                    acceptedKeys: ["articleBeneficiaryId", t("ImportData.BeneficiarId")],
                    templateExamples: templateExamples.beneficiaryId,
                    valueType: ["string"],
                    mandatory: true,
                },
                articleBeneficiaryWorkNature: {
                    label: t("ImportData.WorkNature"),
                    acceptedKeys: ["articleBeneficiaryWorkNature", t("ImportData.WorkNature")],
                    templateExamples: templateExamples.articleWorkNature,
                    valueType: ["string"],
                },
                paidStatus: {
                    label: t("ImportData.PaidStatus"),
                    acceptedKeys: ["paidStatus", t("ImportData.PaidStatus")],
                    templateExamples: templateExamples.paidStatus,
                    valueType: ["string"],
                },
                paidAt: {
                    label: t("ImportData.PaidAt"),
                    acceptedKeys: ["paidAt", t("ImportData.PaidAt")],
                    templateExamples: templateExamples.paidAt,
                    valueType: ["string"],
                },
                articleCompletionStatus: {
                    label: t("ImportData.CompletionStatus"),
                    acceptedKeys: ["completionStatus", t("ImportData.CompletionStatus")],
                    templateExamples: templateExamples.completionStatus,
                    valueType: ["string"],
                },
            },
            relatedDoc:
                "https://presse-helpdesk.crealo.app/fr/article/importer-des-parutions-et-leurs-articles-1hrj8dm/",
        },
        terms: {
            keys: {
                workId: {
                    label: t("ImportData.WorkId"),
                    acceptedKeys: ["workId", "work_id", t("ImportData.WorkId"), ...EXPORT_WORK_HEADERS.workId],
                    templateExamples: templateExamples.workId,
                    valueType: ["string", "number"],
                    mandatory: true,
                },
                coveredTitleIds: {
                    label: t("ImportData.CoveredTitleIds"),
                    acceptedKeys: [
                        "coveredTitleIds",
                        "covered_title_ids",
                        t("ImportData.CoveredTitleIds"),
                        t("ConnectTitle.CoveredTitles"),
                        ...EXPORT_COVERED_TITLE_HEADERS,
                    ],
                    templateExamples: templateExamples.coveredTitleIds,
                    valueType: ["string", "array", "number"],
                },
                beneficiarId: {
                    label: t("ImportData.BeneficiarId"),
                    acceptedKeys: ["beneficiarId", t("ImportData.BeneficiarId")],
                    templateExamples: templateExamples.beneficiaryId,
                    valueType: ["string"],
                    mandatory: true,
                },
                representativeName: {
                    label: t("ImportData.RepresentativeId"),
                    acceptedKeys: ["representativeName", t("ImportData.RepresentativeId")],
                    templateExamples: templateExamples.representativeName,
                    valueType: ["string"],
                },
                reference: {
                    label: t("ImportData.TermReference"),
                    acceptedKeys: ["reference", t("ImportData.TermReference")],
                    templateExamples: templateExamples.reference,
                    valueType: ["string"],
                },
                contractTypology: {
                    label: t("ImportData.ContractTypology"),
                    acceptedKeys: ["contractTypology", t("ImportData.ContractTypology")],
                    templateExamples: templateExamples.contractTypology,
                    valueType: ["string"],
                },
                initialBalance: {
                    label: t("ImportData.InitialBalance"),
                    acceptedKeys: ["initialBalance", t("ImportData.InitialBalance")],
                    templateExamples: templateExamples.initialBalance,
                    valueType: ["string", "number"],
                    floatKey: true,
                },
                initialProvision: {
                    label: t("ImportData.InitialProvision"),
                    acceptedKeys: ["initialProvision", t("ImportData.InitialProvision")],
                    templateExamples: templateExamples.initialProvision,
                    valueType: ["string", "number"],
                    floatKey: true,
                },
                compensate: {
                    label: t("ImportData.Compensate"),
                    acceptedKeys: ["compensate", t("ImportData.Compensate")],
                    templateExamples: templateExamples.compensate,
                    valueType: ["string"],
                    mandatory: true,
                },
                note: {
                    label: t("ImportData.Note"),
                    acceptedKeys: ["note", t("ImportData.Note")],
                    templateExamples: templateExamples.note,
                    valueType: ["string"],
                },
                channelId: {
                    label: t("ImportData.ChannelId"),
                    acceptedKeys: ["channelId", t("ImportData.ChannelId")],
                    templateExamples: templateExamples.channelId,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                calculationBaseColumn: {
                    label: t("ImportData.CalculationBaseColumn"),
                    acceptedKeys: ["calculationBaseColumn", t("ImportData.CalculationBaseColumn")],
                    templateExamples: templateExamples.calculationBaseColumn,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                milestoneBaseColumn: {
                    label: t("ImportData.MilestoneBaseColumn"),
                    acceptedKeys: ["milestoneBaseColumn", t("ImportData.MilestoneBaseColumn")],
                    templateExamples: templateExamples.milestoneBaseColumn,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                formatTypeId: {
                    label: t("ImportData.FormatTypeId"),
                    acceptedKeys: [
                        "formatTypeId",
                        "format_type_id",
                        t("ImportData.FormatTypeId"),
                        ...EXPORT_FORMAT_TYPE_HEADERS.formatTypeId,
                    ],
                    templateExamples: templateExamples.formatTypeId,
                    valueType: ["string", "array", "number"],
                    repeatedKey: true,
                },
                formatTypeName: {
                    label: t("Titles.FormatType"),
                    acceptedKeys: [
                        "formatTypeName",
                        "formatType",
                        t("Titles.FormatType"),
                        ...EXPORT_FORMAT_TYPE_HEADERS.formatTypeName,
                    ],
                    templateExamples: templateExamples.formatType,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                channelPercentage_1: {
                    label: t("ImportData.ChannelFirstPercentage"),
                    acceptedKeys: ["channelPercentage_1", t("ImportData.ChannelFirstPercentage")],
                    templateExamples: templateExamples.channelPercentage_1,
                    valueType: ["string", "array"],
                    floatKey: true,
                    repeatedKey: true,
                },
                channelMilestone_1: {
                    label: t("ImportData.ChannelFirstMilestone"),
                    acceptedKeys: ["channelMilestone_1", t("ImportData.ChannelFirstMilestone")],
                    templateExamples: templateExamples.channelMilestone_1,
                    valueType: ["string", "array"],
                    floatKey: true,
                    repeatedKey: true,
                },
                channelPercentage_2: {
                    label: t("ImportData.ChannelSecondPercentage"),
                    acceptedKeys: ["channelPercentage_2", t("ImportData.ChannelSecondPercentage")],
                    templateExamples: templateExamples.channelPercentage_2,
                    valueType: ["string", "array"],
                    floatKey: true,
                    repeatedKey: true,
                },
                channelMilestone_2: {
                    label: t("ImportData.ChannelSecondMilestone"),
                    acceptedKeys: ["channelMilestone_2", t("ImportData.ChannelSecondMilestone")],
                    templateExamples: templateExamples.channelMilestone_2,
                    valueType: ["string", "array"],
                    floatKey: true,
                    repeatedKey: true,
                },
                channelPercentage_3: {
                    label: t("ImportData.ChannelThirdPercentage"),
                    acceptedKeys: ["channelPercentage_3", t("ImportData.ChannelThirdPercentage")],
                    templateExamples: templateExamples.channelPercentage_3,
                    valueType: ["string", "array"],
                    floatKey: true,
                    repeatedKey: true,
                },
                channelMilestone_3: {
                    label: t("ImportData.ChannelThirdMilestone"),
                    acceptedKeys: ["channelMilestone_3", t("ImportData.ChannelThirdMilestone")],
                    templateExamples: templateExamples.channelMilestone_3,
                    valueType: ["string", "array"],
                    floatKey: true,
                    repeatedKey: true,
                },
                channelPercentage_4: {
                    label: t("ImportData.ChannelFourthPercentage"),
                    acceptedKeys: ["channelPercentage_4", t("ImportData.ChannelFourthPercentage")],
                    templateExamples: templateExamples.channelPercentage_4,
                    valueType: ["string", "array"],
                    floatKey: true,
                    repeatedKey: true,
                },
                channelMilestone_4: {
                    label: t("ImportData.ChannelFourthMilestone"),
                    acceptedKeys: ["channelMilestone_4", t("ImportData.ChannelFourthMilestone")],
                    templateExamples: templateExamples.channelMilestone_4,
                    valueType: ["string", "array"],
                    floatKey: true,
                    repeatedKey: true,
                },
                channelPercentage_5: {
                    label: t("ImportData.ChannelFifthPercentage"),
                    acceptedKeys: ["channelPercentage_5", t("ImportData.ChannelFifthPercentage")],
                    templateExamples: templateExamples.channelPercentage_5,
                    valueType: ["string", "array"],
                    floatKey: true,
                    repeatedKey: true,
                },
                channelMilestone_5: {
                    label: t("ImportData.ChannelFifthMilestone"),
                    acceptedKeys: ["channelMilestone_5", t("ImportData.ChannelFifthMilestone")],
                    templateExamples: templateExamples.channelMilestone_5,
                    valueType: ["string", "array"],
                    floatKey: true,
                    repeatedKey: true,
                },
            },
            relatedDoc: "https://helpdesk.crealo.app/fr/article/importer-des-contrats-1o4zbra/",
        },
        articles: {
            keys: {
                name: {
                    label: t(`ImportData.ArticleName_${publisherType}`),
                    acceptedKeys: [
                        "name",
                        t(`ImportData.ArticleName_${publisherType}`),
                        t("ImportData.TitleName"),
                        ...EXPORT_TITLE_NAME_HEADERS,
                    ],
                    templateExamples: templateExamples.articleName,
                    valueType: ["string"],
                    mandatory: true,
                },
                beneficiaryId: {
                    label: t("ImportData.BeneficiarId"),
                    acceptedKeys: ["beneficiaryId", t("ImportData.BeneficiarId")],
                    templateExamples: templateExamples.beneficiaryId,
                    valueType: ["string"],
                    mandatory: true,
                },
                workNature: {
                    label: t("ImportData.WorkNature"),
                    acceptedKeys: ["workNature", t("ImportData.WorkNature")],
                    templateExamples: templateExamples.articleWorkNature,
                    valueType: ["string"],
                },
                paidStatus: {
                    label: t("ImportData.PaidStatus"),
                    acceptedKeys: ["paidStatus", t("ImportData.PaidStatus")],
                    templateExamples: templateExamples.paidStatus,
                    valueType: ["string"],
                },
                paymentDate: {
                    label: t("ImportData.PaidAt"),
                    acceptedKeys: ["paymentDate", t("ImportData.PaidAt")],
                    templateExamples: templateExamples.paidAt,
                    valueType: ["string"],
                },
                completionStatus: {
                    label: t("ImportData.CompletionStatus"),
                    acceptedKeys: ["completionStatus", t("ImportData.CompletionStatus")],
                    templateExamples: templateExamples.completionStatus,
                    valueType: ["string"],
                },
            },
            relatedDoc: "https://presse-helpdesk.crealo.app/fr/article/importer-des-articles-r9p7c1/",
        },
        provision: {
            keys: {
                provision: {
                    label: t("ConnectTitle.ActualProvision"),
                    acceptedKeys: ["provision", t("ConnectTitle.ActualProvision")],
                    templateExamples: templateExamples.provision,
                    valueType: ["string"],
                },
                reinstatedProvision: {
                    label: t("ConnectTitle.ActualReinstatedProvision"),
                    acceptedKeys: [
                        "reinstatedProvision",
                        "reinstated provision",
                        t("ConnectTitle.ActualReinstatedProvision"),
                    ],
                    templateExamples: templateExamples.reinstatedProvision,
                    valueType: ["string"],
                },
            },
        },
        subscription: {
            keys: {
                titleId: {
                    label: t("Titles.TitleId"),
                    acceptedKeys: ["titleId", "title_id", t("Titles.TitleId")],
                    templateExamples: templateExamples.titleId,
                    valueType: ["string", "number"],
                },
                ean13: {
                    label: t("ImportData.TitleEAN13"),
                    acceptedKeys: ["ean13", t("ImportData.TitleEAN13")],
                    templateExamples: templateExamples.ean13,
                    valueType: ["string"],
                },
                consumptionCount: {
                    label: t("Subscription.NumberOfDownloads"),
                    acceptedKeys: ["consumptionCount", "consumption_count", t("Subscription.NumberOfDownloads")],
                    templateExamples: templateExamples.consumptionCount,
                    valueType: ["string", "number"],
                    mandatory: true,
                },
                avgConsumptionDuration: {
                    label: t("Subscription.AverageConsumptionTime"),
                    acceptedKeys: [
                        "avgConsumptionDuration",
                        "avg_consumption_duration",
                        t("Subscription.AverageConsumptionTime"),
                    ],
                    templateExamples: templateExamples.avgConsumptionDuration,
                    valueType: ["string", "number"],
                    mandatory: true,
                },
            },
        },
        advancePayments: {
            keys: {
                termId: {
                    label: t("ImportData.TermId"),
                    acceptedKeys: ["termId", t("ImportData.TermId")],
                    templateExamples: templateExamples.termId,
                    valueType: ["string"],
                    mandatory: true,
                },
                amount: {
                    label: t("ImportData.Amount"),
                    acceptedKeys: ["amount", t("ImportData.Amount")],
                    templateExamples: templateExamples.advanceAmount,
                    valueType: ["string", "number", "array"],
                    floatKey: true,
                    repeatedKey: true,
                    mandatory: true,
                },
                paidStatus: {
                    label: t("ImportData.PaidStatus"),
                    acceptedKeys: ["paidStatus", t("ImportData.PaidStatus")],
                    templateExamples: templateExamples.paidStatus,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                paidAt: {
                    label: t("ImportData.PaidAt"),
                    acceptedKeys: ["paidAt", t("ImportData.PaidAt")],
                    templateExamples: templateExamples.paidAt,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                comment: {
                    label: t("ImportData.Comment"),
                    acceptedKeys: ["comment", t("ImportData.Comment")],
                    templateExamples: templateExamples.comment,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                customStatus: {
                    label: t("AdvancePayment.CustomStatus"),
                    acceptedKeys: ["customStatus", "statut", "status", t("AdvancePayment.CustomStatus")],
                    templateExamples: templateExamples.customStatus,
                    valueType: ["string", "array"],
                    repeatedKey: true,
                },
                isForfait: {
                    label: t("ImportData.AskIsForfait"),
                    acceptedKeys: ["isForfait", t("ImportData.AskIsForfait")],
                    valueType: ["number", "string"],
                    templateExamples: templateExamples.isForfait,
                },
            },
            relatedDoc: "https://helpdesk.crealo.app/fr/article/importer-des-a-valoir-1w67si5/",
        },
        stock: {
            keys: {
                ean13: {
                    label: t("ImportData.TitleEAN13"),
                    acceptedKeys: ["ean13", t("ImportData.TitleEAN13")],
                    templateExamples: templateExamples.ean13,
                    valueType: ["string"],
                    mandatory: true,
                },
                inventory: {
                    label: t("ImportData.Stock"),
                    acceptedKeys: ["inventory", t("ImportData.Stock")],
                    templateExamples: templateExamples.inventory,
                    valueType: ["string"],
                },
                pressInventory: {
                    label: t("ImportData.PressInventory"),
                    acceptedKeys: ["pressInventory", t("ImportData.PressInventory")],
                    templateExamples: templateExamples.pressInventory,
                    valueType: ["string"],
                },
                scrappedInventory: {
                    label: t("ImportData.ScrappedInventory"),
                    acceptedKeys: ["scrappedInventory", t("ImportData.ScrappedInventory")],
                    templateExamples: templateExamples.scrappedInventory,
                    valueType: ["string"],
                },
                printedInventory: {
                    label: t("ImportData.PrintedInventory"),
                    acceptedKeys: ["printedInventory", t("ImportData.PrintedInventory")],
                    templateExamples: templateExamples.printedInventory,
                    valueType: ["string"],
                },
            },
            relatedDoc: "https://helpdesk.crealo.app/fr/article/importer-des-donnees-de-stock-1qwsy1w/",
        },
        forfait: {
            keys: {
                ean13: {
                    label: t("Management.ImportEAN13"),
                    acceptedKeys: ["ean13", t("Management.ImportEAN13")],
                    templateExamples: templateExamples.ean13,
                    valueType: ["string"],
                    mandatory: true,
                },
                beneficiaryId: {
                    label: t("Management.BeneficiaryId"),
                    acceptedKeys: ["beneficiaryId", t("Management.BeneficiaryId")],
                    templateExamples: templateExamples.beneficiaryId,
                    valueType: ["string"],
                    mandatory: true,
                },
                // A package sale is a lump sum, so it fills the turnover its contract is locked on.
                turnover: {
                    label: t("Management.ForfaitAmount"),
                    acceptedKeys: ["turnover", "amount", t("Management.ForfaitAmount")],
                    templateExamples: templateExamples.forfaitAmount,
                    valueType: ["string", "number"],
                    floatKey: true,
                    mandatory: true,
                },
                note: {
                    label: t("Management.ImportNote"),
                    acceptedKeys: ["note", t("Management.ImportNote")],
                    templateExamples: templateExamples.note,
                    valueType: ["string"],
                },
                date: {
                    label: t("Management.SaleDate"),
                    acceptedKeys: ["date", t("Management.SaleDate")],
                    templateExamples: templateExamples.saleDate,
                    immaterielKey: ["Date de mise en vente"],
                    immaterielLabel: t("Management.SaleDate"),
                    valueType: ["string"],
                },
            },
            relatedDoc: "https://helpdesk.crealo.app/fr/article/importer-des-ventes-1huz39s/?bust=1697813385419",
        },
        works: {
            keys: {
                name: {
                    label: t("Works.WorkName"),
                    acceptedKeys: [
                        "name",
                        "workName",
                        "work_name",
                        t("Works.WorkName"),
                        t("ImportData.WorkName"),
                        ...EXPORT_WORK_HEADERS.workName,
                    ],
                    templateExamples: templateExamples.workName,
                    valueType: ["string"],
                    mandatory: true,
                },
                workCode: {
                    label: t("ImportData.WorkCode"),
                    acceptedKeys: [
                        "workCode",
                        "work_code",
                        t("ImportData.WorkCode"),
                        t("Works.WorkCode"),
                        ...EXPORT_WORK_HEADERS.workCode,
                    ],
                    templateExamples: templateExamples.workCode,
                    valueType: ["string"],
                },
                artworkNature: {
                    label: t("ImportData.ArtworkNature"),
                    acceptedKeys: [
                        "artworkNature",
                        t("ImportData.ArtworkNature"),
                        ...EXPORT_WORK_HEADERS.artworkNature,
                    ],
                    templateExamples: templateExamples.artworkNature,
                    valueType: ["string"],
                },
                analysisCode: {
                    label: t("ImportData.TitleAnalysisCode"),
                    acceptedKeys: [
                        "analysisCode",
                        t("ImportData.TitleAnalysisCode"),
                        ...EXPORT_WORK_HEADERS.analysisCode,
                    ],
                    templateExamples: templateExamples.analysisCode,
                    valueType: ["string"],
                },
            },
            relatedDoc: "",
        },
        balance: {
            keys: {
                termId: {
                    label: t("ImportData.TermId"),
                    acceptedKeys: ["termId", t("ImportData.TermId")],
                    templateExamples: templateExamples.termId,
                    valueType: ["number"],
                    mandatory: true,
                },
                balance: {
                    label: t("ImportData.NewBalance"),
                    acceptedKeys: ["balance", t("ImportData.NewBalance")],
                    templateExamples: templateExamples.initialBalance,
                    valueType: ["number"],
                    mandatory: true,
                },
                comment: {
                    label: t("ImportData.Comment"),
                    acceptedKeys: ["balance", t("ImportData.Comment")],
                    templateExamples: templateExamples.comment,
                    valueType: ["string"],
                    mandatory: true,
                },
            },
            relatedDoc: "", // ADD LINK ONCE IT'S PUBLISHED
        },
    };

    if (type === "beneficiars" && conditions) {
        const keys = data.beneficiars.keys as Record<string, TKeyData>;

        // Remove VAT column for countries without VAT (e.g. Canada)
        if (conditions.hasVat === false) {
            delete keys.vat;
        }

        // Remove URSSAF socialProfileType for countries that don't use it (e.g. UK, Canada)
        if (conditions.urssaf === false) {
            delete keys.socialProfileType;
        }

        // Replace IBAN/BIC with country-specific banking fields when the country doesn't use IBAN
        const bankingFields = conditions.bankingFields;
        if (bankingFields && !bankingFields.includes("iban")) {
            const metaFields = bankingFields.filter((f) => f !== "iban" && f !== "bic");
            const newKeys: Record<string, TKeyData> = {};
            let insertedFields = false;
            for (const [k, v] of Object.entries(keys)) {
                if (k === "iban" || k === "swift") {
                    if (!insertedFields) {
                        for (const fieldKey of metaFields) {
                            const i18nKey = toBankingFieldI18nKey(fieldKey);
                            newKeys[fieldKey] = {
                                label: t(i18nKey),
                                acceptedKeys: [fieldKey, t(i18nKey)],
                                templateExamples: (templateExamples as Record<string, string>)[fieldKey],
                                valueType: ["string"],
                            };
                        }
                        insertedFields = true;
                    }
                    // omit iban and swift from the template
                } else {
                    newKeys[k] = v;
                }
            }
            (data.beneficiars as any).keys = newKeys;
        }

        // Insert international banking columns right after ribOwnerName (grouped with other banking fields)
        if (conditions.internationalBankingEnabled) {
            const currentKeys = (data.beneficiars as any).keys as Record<string, TKeyData>;
            (data.beneficiars as any).keys = insertInternationalBankingColumns(currentKeys);
        }
    }

    if (type === "titles") {
        const titlesData = data.titles as TImportData;
        const keys = { ...titlesData.keys };
        if (publisherType === "book") {
            delete keys.analysisCode;
        }
        if (!conditions?.isBulkUpdate) {
            delete keys.channelISBN;
            delete keys.channelBookTitle;
            delete keys.channelReleaseDate;
        }
        return { ...titlesData, keys } as (typeof data)[T];
    }

    if (type === "terms" && publisherType && publisherType !== "book") {
        const termsData = data.terms as TImportData;
        const keys = { ...termsData.keys };
        delete keys.coveredTitleIds;
        return { ...termsData, keys } as (typeof data)[T];
    }

    return data[type] as (typeof data)[T];
};

const getArray = (typeData: any, item: string) => {
    const data: any = [];
    Object.keys(typeData).forEach((key) => {
        data.push(typeData[key][item]);
    });
    return item === "acceptedKeys" ? data.flat() : data;
};

export const getPropertyObject = (typeData: any, item: string) => {
    const data = {};
    Object.keys(typeData).forEach((key) => {
        data[key] = typeData[key][item];
    });

    return data;
};

/**
 * Accepted keys include labels in both FR and EN so a file whose headers are in the other language still maps.
 */
export const getImportKeys = (...args: Parameters<typeof buildImportKeys>): ReturnType<typeof buildImportKeys> => {
    if (args[2] !== "acceptedKeys") return buildImportKeys(...args);
    return mergeAcceptedKeys([
        buildImportKeys(...args),
        ...getMappingLanguages().map((lng) => withLanguage(lng, () => buildImportKeys(...args))),
    ]);
};

const buildImportKeys = (
    type: TImportDataDictionary,
    conditions: TImportConditions,
    item:
        | "label"
        | "acceptedKeys"
        | "immaterielKey"
        | "immaterielLabel"
        | "templateExamples"
        | "valueType"
        | "mandatory"
        | "repeatedKey",
    publisherType: TPortalType | undefined,
    forceArray?: boolean,
) => {
    if (["publications", "publicationsWithArticles", "articles"].includes(type) && !publisherType) return;
    let typeData = getBasicImportTypeData(type, publisherType, conditions).keys as TImportData["keys"];
    const customFields = conditions.customFields;

    switch (type) {
        case "sales":
        case "forfait":
            typeData = addSaleColumns(typeData, conditions, item);
            return forceArray ? getArray(typeData, item) : getPropertyObject(typeData, item);
        case "titles":
            typeData = addTitleColumns(typeData, conditions, customFields, publisherType);
            return forceArray ? getArray(typeData, item) : getPropertyObject(typeData, item);
        case "beneficiars":
            typeData = addBeneficiaryColumns(typeData, conditions, customFields);
            return forceArray ? getArray(typeData, item) : getPropertyObject(typeData, item);
        case "publications":
            typeData = addPublicationColumns(typeData, conditions, publisherType);
            return forceArray ? getArray(typeData, item) : getPropertyObject(typeData, item);
        case "publicationsWithArticles":
            typeData = addPublicationWithArticlesColumns(typeData, conditions, publisherType);
            return forceArray ? getArray(typeData, item) : getPropertyObject(typeData, item);
        case "terms":
            typeData = addTermColumns(typeData, conditions, customFields);
            return forceArray ? getArray(typeData, item) : getPropertyObject(typeData, item);
        case "articles":
            typeData = addArticleColumns(typeData, conditions);
            return forceArray ? getArray(typeData, item) : getPropertyObject(typeData, item);
        case "provision":
            typeData = addProvisionColumns(typeData, conditions);
            return forceArray ? getArray(typeData, item) : getPropertyObject(typeData, item);
        case "works":
            typeData = addWorkColumns(typeData, conditions);
            return forceArray ? getArray(typeData, item) : getPropertyObject(typeData, item);
        default:
            return forceArray ? getArray(typeData, item) : getPropertyObject(typeData, item);
    }
};

/** Headers that must stay unique. Repeated channel columns are left out on purpose. */
export const getSingleValueCsvHeaders = (
    type: TImportDataDictionary,
    conditions: TImportConditions,
    publisherType: TPortalType | undefined,
): string[] | undefined => {
    const labels = getImportKeys(type, conditions, "label", publisherType);
    const accepted = getImportKeys(type, conditions, "acceptedKeys", publisherType);
    const repeated = getImportKeys(type, conditions, "repeatedKey", publisherType);
    if (
        !labels ||
        Array.isArray(labels) ||
        !accepted ||
        Array.isArray(accepted) ||
        !repeated ||
        Array.isArray(repeated)
    ) {
        return undefined;
    }

    const names: string[] = [];
    for (const key of Object.keys(labels)) {
        if (repeated[key]) continue;
        const label = labels[key];
        if (typeof label === "string" && label.trim()) names.push(label);
        const acceptedNames = accepted[key];
        if (!Array.isArray(acceptedNames)) continue;
        for (const acceptedName of acceptedNames) {
            if (typeof acceptedName === "string" && acceptedName.trim()) names.push(acceptedName);
        }
    }

    return [...new Set(names)];
};

const getCustomFieldValueType = (fieldType: string) => {
    if (["string", "longstring", "date", "link"].includes(fieldType)) {
        return ["string"];
    } else if (fieldType === "boolean") {
        return ["number"];
    } else {
        return ["string", "number"];
    }
};

const addSaleColumns = (
    typeData: Record<string, Omit<TKeyData, "templateExamples">>,
    conditions: TImportConditions,
    item,
) => {
    if (["immaterielKey"].includes(item)) {
        return getArray(typeData, "immaterielKey");
    }

    if (conditions.usesCountryMarket) {
        typeData = insertPairInObject(typeData, "turnover", {
            key: "countryMarket",
            value: {
                label: t("Management.CountrySaleFormat"),
                acceptedKeys: ["countryMarket", t("Management.CountrySaleFormat")],
                valueType: ["string"],
            },
        });
    }

    if (conditions.usesOverridenPriceSales) {
        typeData = insertPairInObject(typeData, "turnover", {
            key: "overridenPrice",
            value: {
                label: t("Management.ImportSpecificSalesPrice"),
                acceptedKeys: ["overridenPrice", t("Management.ImportSpecificSalesPrice")],
                valueType: ["string", "number"],
                floatKey: true,
            },
        });
        typeData = insertPairInObject(typeData, "turnover", {
            key: "overridenPriceCurrency",
            value: {
                label: t("Management.ImportSpecificSalesPriceCurrency"),
                acceptedKeys: ["overridenPriceCurrency", t("Management.ImportSpecificSalesPriceCurrency")],
                valueType: ["string"],
            },
        });
    }

    if (conditions.choseAllChannels) {
        typeData = insertPairInObject(typeData, "returnedSales", {
            key: "channelId",
            value: {
                label: t("Channels.Id"),
                acceptedKeys: ["channelId", t("Channels.Id")],
                valueType: ["string", "array"],
            },
        });
    }

    // Expose the custom turnover slots as mappable columns only when the company configured
    // a label for them in settings. The internal key matches the ImportSalesRow key so the
    // backend routes the mapped CSV column to the right title_channel_sales.turnover_1/2.
    if (conditions.customTurnoverLabels?.turnover_1) {
        typeData["turnover_1"] = {
            label: conditions.customTurnoverLabels.turnover_1,
            acceptedKeys: ["turnover_1", conditions.customTurnoverLabels.turnover_1],
            valueType: ["string", "number"],
            floatKey: true,
        };
    }

    if (conditions.customTurnoverLabels?.turnover_2) {
        typeData["turnover_2"] = {
            label: conditions.customTurnoverLabels.turnover_2,
            acceptedKeys: ["turnover_2", conditions.customTurnoverLabels.turnover_2],
            valueType: ["string", "number"],
            floatKey: true,
        };
    }

    if (conditions.usesDistributorSalesData) {
        typeData["distributorInvoicingPercentage"] = {
            label: t("Management.Rate"),
            acceptedKeys: ["distributorInvoicingPercentage", t("Management.Rate")],
            valueType: ["string", "array", "number"],
            floatKey: true,
        };
        typeData["distributorGrossPrice"] = {
            label: t("Management.DistributorGrossPrice"),
            acceptedKeys: ["distributorGrossPrice", t("Management.DistributorGrossPrice")],
            valueType: ["string", "number"],
            floatKey: true,
        };
    }

    if (conditions.isCollectiveManagementChannel) {
        typeData["collectiveManagementKind"] = {
            label: t("ImportData.CollectiveManagementKind"),
            acceptedKeys: ["collectiveManagementKind", t("ImportData.CollectiveManagementKind")],
            valueType: ["string"],
            // Required only when importing on a single CM channel. All-channels import
            // would otherwise block files that have no kind column for non-CM rows.
            mandatory: !conditions.choseAllChannels,
        };
    }

    return typeData;
};

const addWorkColumns = (
    typeData: Record<string, Omit<TKeyData, "templateExamples">>,
    conditions: TImportConditions,
) => {
    if (conditions.usesTitleCompensation) {
        typeData = insertPairInObject(typeData, "workCode", {
            key: "compensationGroup",
            value: {
                label: t("ImportData.CompensationGroup"),
                acceptedKeys: [
                    "compensationGroup",
                    t("ImportData.CompensationGroup"),
                    ...EXPORT_WORK_HEADERS.compensationGroup,
                ],
                templateExamples: templateExamples.compensationGroup,
                valueType: ["string"],
            },
        });
    }

    if (conditions.urssaf === false) {
        delete typeData.artworkNature;
    }

    if (conditions.usesMultipleBrands) {
        typeData = insertPairInObject(typeData, "analysisCode", {
            key: "associatedBrand",
            value: {
                label: t("ImportData.AssociatedBrand"),
                acceptedKeys: [
                    "associatedBrand",
                    t("ImportData.AssociatedBrand"),
                    ...EXPORT_WORK_HEADERS.associatedBrand,
                ],
                templateExamples: templateExamples.brand,
                valueType: ["string"],
            },
        });
    }

    return typeData;
};

const addTitleColumns = (
    typeData: Record<string, Omit<TKeyData, "templateExamples">>,
    conditions: TImportConditions,
    customFields: ICustomField[] | undefined,
    publisherType?: TPortalType,
) => {
    if (conditions.usesMultipleBrands && publisherType !== "book") {
        typeData = insertPairInObject(typeData, "ean13", {
            key: "associatedBrand",
            value: {
                label: t("ImportData.AssociatedBrand"),
                acceptedKeys: ["associatedBrand", t("ImportData.AssociatedBrand")],
                valueType: ["string"],
            },
        });
    }

    if (conditions.usesMultipleCurrencies) {
        typeData = insertPairInObject(typeData, "price", {
            key: "currency",
            value: {
                label: t("ImportData.Currency"),
                acceptedKeys: ["currency", t("ImportData.Currency")],
                valueType: ["string"],
            },
        });

        typeData = insertPairInObject(typeData, "channelPrice", {
            key: "channelCurrency",
            value: {
                label: t("ImportData.TitleChannelCurrency"),
                acceptedKeys: ["channelCurrency", t("ImportData.TitleChannelCurrency")],
                valueType: ["string", "array"],
                repeatedKey: true,
            },
        });
    }

    if (conditions.allowsSubscriptionChannels) {
        typeData = insertPairInObject(typeData, "channelPrice", {
            key: "channelSubscriptionCoefficient",
            value: {
                label: t("ImportData.ChannelSubscriptionCoefficient"),
                acceptedKeys: ["channelSubscriptionCoefficient", t("ImportData.ChannelSubscriptionCoefficient")],
                valueType: ["string", "array", "number"],
                floatKey: true,
                repeatedKey: true,
            },
        });
    }

    // Custom turnover opening cumulatives: same gating as the title form / sales import.
    if (conditions.customTurnoverLabels?.turnover_1) {
        typeData["turnover_1"] = {
            label: conditions.customTurnoverLabels.turnover_1,
            acceptedKeys: ["turnover_1", conditions.customTurnoverLabels.turnover_1],
            valueType: ["string", "array", "number"],
            floatKey: true,
            repeatedKey: true,
        };
    }

    if (conditions.customTurnoverLabels?.turnover_2) {
        typeData["turnover_2"] = {
            label: conditions.customTurnoverLabels.turnover_2,
            acceptedKeys: ["turnover_2", conditions.customTurnoverLabels.turnover_2],
            valueType: ["string", "array", "number"],
            floatKey: true,
            repeatedKey: true,
        };
    }

    if (customFields && customFields?.length > 0) {
        customFields.forEach((field: ICustomField) => {
            typeData[`meta:${field.uuid}`] = {
                label: field.name,
                acceptedKeys: [`meta:${field.uuid}`, field.name],
                valueType: getCustomFieldValueType(field.valueType),
            };
        });
    }

    return typeData;
};

const addBeneficiaryColumns = (
    typeData: Record<string, Omit<TKeyData, "templateExamples">>,
    conditions: TImportConditions,
    customFields: ICustomField[] | undefined,
) => {
    if (conditions.usesDirectPrecount) {
        typeData = insertPairInObject(typeData, "socialSecurityNumber", {
            key: "precount",
            value: {
                label: t("ImportData.BenPrecount"),
                acceptedKeys: ["precount", t("ImportData.BenPrecount")],
                valueType: ["string"],
            },
        });
    } else if (conditions.urssaf !== false) {
        typeData = insertPairInObject(typeData, "socialSecurityNumber", {
            key: "socialProfileType",
            value: {
                label: t("ImportData.BenSocialProfileType"),
                acceptedKeys: ["socialProfileType", t("ImportData.BenSocialProfileType")],
                valueType: ["string"],
            },
        });
    }

    if (customFields && customFields.length > 0) {
        customFields.forEach((field: ICustomField) => {
            typeData[`meta:${field.uuid}`] = {
                label: field.name,
                acceptedKeys: [`meta:${field.uuid}`, field.name],
                valueType: getCustomFieldValueType(field.valueType),
            };
        });
    }
    return typeData;
};

const addPublicationColumns = (
    typeData: Record<string, Omit<TKeyData, "templateExamples">>,
    conditions: TImportConditions,
    publisherType: TPortalType | undefined,
) => {
    if (conditions.usesISSN) {
        typeData = insertPairInObject(typeData, "name", {
            key: "parutionCode",
            value: {
                label: t("ImportData.TitleParutionCode"),
                acceptedKeys: ["parutionCode", t("ImportData.TitleParutionCode")],
                valueType: ["string"],
                mandatory: true,
            },
        });
        typeData = insertPairInObject(typeData, "parutionCode", {
            key: "issn",
            value: {
                label: t("ImportData.ISSN"),
                acceptedKeys: ["issn", t("ImportData.ISSN")],
                valueType: ["string"],
                mandatory: true,
            },
        });
    } else {
        typeData = insertPairInObject(typeData, "name", {
            key: "issueUniqueCode",
            value: {
                label: t(`ImportData.UniqueCode_${publisherType}`),
                acceptedKeys: ["issueUniqueCode", t(`ImportData.UniqueCode_${publisherType}`)],
                valueType: ["string"],
                mandatory: true,
            },
        });
    }
    if (conditions.usesFolio || conditions.pressUsesBothChannels) {
        typeData = insertPairInObject(typeData, "artworkNature", {
            key: "packagePrice",
            value: {
                label: t("ImportData.ParutionPackagePrice"),
                acceptedKeys: ["packagePrice", t("ImportData.ParutionPackagePrice")],
                templateExamples: templateExamples.packagePrice,
                valueType: ["string", "number"],
                floatKey: true,
            },
        });
    }

    if (conditions.urssaf === false) {
        delete typeData.artworkNature;
    }

    return typeData;
};

const addPublicationWithArticlesColumns = (
    typeData: Record<string, Omit<TKeyData, "templateExamples">>,
    conditions: TImportConditions,
    publisherType: TPortalType | undefined,
) => {
    if (conditions.usesISSN) {
        typeData = insertPairInObject(typeData, "issueName", {
            key: "parutionCode",
            value: {
                label: t("ImportData.TitleParutionCode"),
                acceptedKeys: ["parutionCode", t("ImportData.TitleParutionCode")],
                valueType: ["string"],
            },
        });
        typeData = insertPairInObject(typeData, "parutionCode", {
            key: "issn",
            value: {
                label: t("ImportData.ISSN"),
                acceptedKeys: ["issn", t("ImportData.ISSN")],
                valueType: ["string"],
            },
        });
    } else {
        typeData = insertPairInObject(typeData, "issueName", {
            key: "issueUniqueCode",
            value: {
                label: t(`ImportData.UniqueCode_${publisherType}`),
                acceptedKeys: ["issueUniqueCode", t(`ImportData.UniqueCode_${publisherType}`)],
                valueType: ["string"],
            },
        });
    }

    if (conditions.pressUsesBothChannels) {
        typeData = insertPairInObject(typeData, "articleBeneficiaryWorkNature", {
            key: "channelId",
            value: {
                label: t("ImportData.ChannelId"),
                acceptedKeys: ["channelId", t("ImportData.ChannelId")],
                valueType: ["string", "array"],
            },
        });

        typeData = insertPairInObject(typeData, "channelId", {
            key: "folioQuantity",
            value: {
                label: t("ImportData.ArticleQuantity"),
                acceptedKeys: ["folioQuantity", t("ImportData.ArticleQuantity")],
                valueType: ["number", "undefined"],
            },
        });

        typeData = insertPairInObject(typeData, "folioQuantity", {
            key: "folioPrice",
            value: {
                label: t("ImportData.TitlePrice"),
                acceptedKeys: ["folioPrice", t("ImportData.TitlePrice")],
                valueType: ["string", "number", "undefined"],
                floatKey: true,
            },
        });

        typeData = insertPairInObject(typeData, "folioPrice", {
            key: "grossDuty",
            value: {
                label: t("ImportData.GrossDuty"),
                acceptedKeys: ["grossDuty", t("ImportData.GrossDuty")],
                valueType: ["number", "string", "undefined"],
                floatKey: true,
            },
        });
    } else if (conditions.usesFolio) {
        typeData = insertPairInObject(typeData, "articleBeneficiaryWorkNature", {
            key: "folioQuantity",
            value: {
                label: t("ImportData.ArticleQuantity"),
                acceptedKeys: ["folioQuantity", t("ImportData.ArticleQuantity")],
                valueType: ["number", "undefined"],
            },
        });

        typeData = insertPairInObject(typeData, "folioQuantity", {
            key: "folioPrice",
            value: {
                label: t("ImportData.TitlePrice"),
                acceptedKeys: ["folioPrice", t("ImportData.TitlePrice")],
                valueType: ["string", "number", "undefined"],
                floatKey: true,
            },
        });
    } else {
        typeData = insertPairInObject(typeData, "articleBeneficiaryWorkNature", {
            key: "grossDuty",
            value: {
                label: t("ImportData.GrossDuty"),
                acceptedKeys: ["grossDuty", t("ImportData.GrossDuty")],
                valueType: ["number", "string", "undefined"],
                floatKey: true,
            },
        });
    }

    return typeData;
};

/**
 * The first field keeps its name. A later field with the same name uses `meta:<uuid>`,
 * matching the export, so two columns never share a header.
 */
export const termCustomFieldImportColumns = (fields: readonly ICustomField[]) => {
    const taken = new Set<string>();
    return fields.map((field) => {
        const label = field.name.trim();
        const identifier = `meta:${field.uuid}`;
        const header = label && !taken.has(label) ? label : identifier;
        taken.add(header);
        return {
            uuid: field.uuid,
            header,
            acceptedKeys: header === identifier ? [identifier] : [identifier, header],
            valueType: field.valueType,
        };
    });
};

const addTermColumns = (
    typeData: Record<string, Omit<TKeyData, "templateExamples">>,
    conditions: TImportConditions,
    customFields?: ICustomField[],
) => {
    if (conditions.usesAdvanceCompensation) {
        typeData = insertPairInObject(typeData, "reference", {
            key: "compensationGroup",
            value: {
                label: t("ImportData.CompensationGroup"),
                acceptedKeys: ["compensationGroup", t("ImportData.CompensationGroup")],
                valueType: ["string"],
            },
        });
    }

    if (conditions.usesInitialAdvance) {
        typeData = insertPairInObject(typeData, "reference", {
            key: "initialAdvance",
            value: {
                label: t("ImportData.InitialAdvance"),
                acceptedKeys: ["initialAdvance", t("ImportData.InitialAdvance")],
                valueType: ["string", "number"],
            },
        });
    }

    if (conditions.usesCumulativeRoyalties) {
        typeData = insertPairInObject(typeData, "reference", {
            key: "cumulativeRights",
            value: {
                label: t("ImportData.CumulativeRights"),
                acceptedKeys: ["cumulativeRights", t("ImportData.CumulativeRights")],
                valueType: ["string", "number"],
                floatKey: true,
            },
        });
    }

    for (const column of termCustomFieldImportColumns(customFields ?? [])) {
        typeData[`meta:${column.uuid}`] = {
            label: column.header,
            acceptedKeys: column.acceptedKeys,
            valueType: getCustomFieldValueType(column.valueType),
        };
    }
    return typeData;
};

const addArticleColumns = (
    typeData: Record<string, Omit<TKeyData, "templateExamples">>,
    conditions: TImportConditions,
) => {
    if (conditions.pressUsesBothChannels) {
        typeData = insertPairInObject(typeData, "workNature", {
            key: "channelId",
            value: {
                label: t("ImportData.ChannelId"),
                acceptedKeys: ["channelId", t("ImportData.ChannelId")],
                valueType: ["string", "array"],
            },
        });

        typeData = insertPairInObject(typeData, "channelId", {
            key: "folioQuantity",
            value: {
                label: t("ImportData.ArticleQuantity"),
                acceptedKeys: ["folioQuantity", t("ImportData.ArticleQuantity")],
                valueType: ["string", "array"],
            },
        });

        typeData = insertPairInObject(typeData, "folioQuantity", {
            key: "folioPrice",
            value: {
                label: t("ImportData.TitlePrice"),
                acceptedKeys: ["folioPrice", t("ImportData.TitlePrice")],
                valueType: ["string", "number", "array"],
                floatKey: true,
            },
        });

        typeData = insertPairInObject(typeData, "folioPrice", {
            key: "grossDuty",
            value: {
                label: t("ImportData.GrossDuty"),
                acceptedKeys: ["grossDuty", t("ImportData.GrossDuty")],
                valueType: ["string", "number", "array"],
                floatKey: true,
            },
        });
    } else if (conditions.usesFolio) {
        typeData = insertPairInObject(typeData, "workNature", {
            key: "folioQuantity",
            value: {
                label: t("ImportData.ArticleQuantity"),
                acceptedKeys: ["folioQuantity", t("ImportData.ArticleQuantity")],
                valueType: ["string", "array"],
            },
        });

        typeData = insertPairInObject(typeData, "folioQuantity", {
            key: "folioPrice",
            value: {
                label: t("ImportData.TitlePrice"),
                acceptedKeys: ["folioPrice", t("ImportData.TitlePrice")],
                valueType: ["string", "number", "array"],
                floatKey: true,
            },
        });
    } else {
        typeData = insertPairInObject(typeData, "workNature", {
            key: "grossDuty",
            value: {
                label: t("ImportData.GrossDuty"),
                acceptedKeys: ["grossDuty", t("ImportData.GrossDuty")],
                valueType: ["string", "number", "array"],
                floatKey: true,
            },
        });
    }
    return typeData;
};

const addProvisionColumns = (
    typeData: Record<string, Omit<TKeyData, "templateExamples">>,
    conditions: TImportConditions,
) => {
    if (conditions.isProvisionAddedOnTitle) {
        typeData = insertPairInObject(typeData, "provision", {
            key: "ean13",
            value: {
                label: t("ImportData.TitleEAN13"),
                acceptedKeys: ["ean13", t("ImportData.TitleEAN13")],
                templateExamples: templateExamples.ean13,
                valueType: ["string"],
                mandatory: true,
            },
        });
    } else {
        typeData = insertPairInObject(typeData, "provision", {
            key: "termId",
            value: {
                label: t("ImportData.TitleTermId"),
                acceptedKeys: ["termId", t("ImportData.TitleTermId")],
                valueType: ["string"],
                mandatory: true,
            },
        });
    }
    return typeData;
};

export const getImportTitles = (
    type: TImportDataDictionary,
    conditions: TImportConditions,
    publisherType: TPortalType | undefined,
) => {
    const typeData = getBasicImportTypeData(type, publisherType, conditions).keys;
    const titles = getArray(typeData, "label");
    const examples = getArray(typeData, "templateExamples");
    const customFields = conditions.customFields;

    switch (type) {
        case "titles":
            return addTitleExamples(typeData, titles, examples, conditions, customFields, publisherType);
        case "publications":
            return addPublicationExamples(typeData, titles, examples, conditions, publisherType);
        case "publicationsWithArticles":
            return addPublicationWithArticlesExamples(typeData, titles, examples, conditions, publisherType);
        case "beneficiars":
            return addBeneficiaryExamples(typeData, titles, examples, conditions, customFields);
        case "sales":
        case "forfait":
            return addSalesExamples(typeData, titles, examples, conditions);
        case "terms": {
            const [termTitles, termExamples] = addTermsExamples(
                addTermColumns(typeData, conditions, customFields),
                titles,
                examples,
                conditions,
            );
            const termCustomFieldColumns = termCustomFieldImportColumns(customFields ?? []);
            return [
                [...termTitles, ...termCustomFieldColumns.map((column) => column.header)],
                [...termExamples, ...termCustomFieldColumns.map((column) => getCustomFieldExample(column.valueType))],
            ];
        }
        case "articles":
            return addArticleExamples(typeData, titles, examples, conditions);
        case "provision":
            return addProvisionExamples(typeData, titles, examples, conditions);
        case "works": {
            const workTypeData = addWorkColumns(typeData, conditions);
            return [getArray(workTypeData, "label"), getArray(workTypeData, "templateExamples")];
        }
        default:
            return [[...titles], [...examples]];
    }
};

/**
 * Returns placement of new conditional column.
 */
const getBaseKeyIndex = (titles, reference: string) => {
    return titles.findIndex((item) => item === reference) + 1;
};

const addTitleExamples = (typeData, titles, examples, conditions, customFields, publisherType?: TPortalType) => {
    // For logic: you look for the index of a specific label and insert right afterwards the new example.
    // We always look at the index of titles, as titles and examples should be symmetrical.
    if (conditions.usesMultipleBrands && publisherType !== "book") {
        titles.splice(getBaseKeyIndex(titles, typeData.price.label), 0, t("ImportData.AssociatedBrand"));
        examples.splice(getBaseKeyIndex(titles, typeData.price.label), 0, templateExamples.brand);
    }

    if (conditions.usesMultipleCurrencies) {
        titles.splice(getBaseKeyIndex(titles, typeData.price.label), 0, t("ImportData.Currency"));
        examples.splice(getBaseKeyIndex(titles, typeData.price.label), 0, templateExamples.currency);

        titles.splice(getBaseKeyIndex(titles, typeData.channelPrice.label), 0, t("ImportData.TitleChannelCurrency"));
        examples.splice(getBaseKeyIndex(titles, typeData.channelPrice.label), 0, templateExamples.channelCurrency);
    }

    if (conditions.customTurnoverLabels?.turnover_1) {
        titles = [...titles, conditions.customTurnoverLabels.turnover_1];
        examples = [...examples, templateExamples.turnover];
    }

    if (conditions.customTurnoverLabels?.turnover_2) {
        titles = [...titles, conditions.customTurnoverLabels.turnover_2];
        examples = [...examples, templateExamples.turnover];
    }

    if (customFields.length > 0) {
        customFields.forEach((field) => {
            titles = [...titles, field.name];
            examples = [...examples, getCustomFieldExample(field.valueType)];
        });
    }

    return [titles, examples];
};

const addPublicationExamples = (typeData, titles, examples, conditions, publisherType) => {
    if (conditions.usesISSN) {
        titles.splice(getBaseKeyIndex(titles, typeData.name.label), 0, t("ImportData.ISSN"));
        examples.splice(getBaseKeyIndex(titles, typeData.name.label), 0, templateExamples.issn);

        titles.splice(getBaseKeyIndex(titles, t("ImportData.ISSN")), 0, t("ImportData.TitleParutionCode"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.ISSN")), 0, templateExamples.parutionCode);
    } else {
        titles.splice(getBaseKeyIndex(titles, typeData.name.label), 0, t(`ImportData.UniqueCode_${publisherType}`));
        examples.splice(getBaseKeyIndex(titles, typeData.name.label), 0, templateExamples.uniqueCode);
    }

    if (conditions.usesFolio || conditions.pressUsesBothChannels) {
        titles.splice(getBaseKeyIndex(titles, typeData.artworkNature.label), 0, t("ImportData.ParutionPackagePrice"));
        examples.splice(getBaseKeyIndex(titles, typeData.artworkNature.label), 0, templateExamples.packagePrice);
    }

    return [[...titles], [...examples]];
};

const addPublicationWithArticlesExamples = (typeData, titles, examples, conditions, publisherType) => {
    if (conditions.usesISSN) {
        titles.splice(getBaseKeyIndex(titles, typeData.issueName.label), 0, t("ImportData.ISSN"));
        examples.splice(getBaseKeyIndex(titles, typeData.issueName.label), 0, templateExamples.issn);

        titles.splice(getBaseKeyIndex(titles, t("ImportData.ISSN")), 0, t("ImportData.TitleParutionCode"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.ISSN")), 0, templateExamples.parutionCode);
    } else {
        titles.splice(
            getBaseKeyIndex(titles, typeData.issueName.label),
            0,
            t(`ImportData.UniqueCode_${publisherType}`),
        );
        examples.splice(getBaseKeyIndex(titles, typeData.issueName.label), 0, templateExamples.uniqueCode);
    }

    if (conditions.pressUsesBothChannels) {
        titles.splice(
            getBaseKeyIndex(titles, typeData.articleBeneficiaryWorkNature.label),
            0,
            t("ImportData.ChannelId"),
        );
        examples.splice(
            getBaseKeyIndex(titles, typeData.articleBeneficiaryWorkNature.label),
            0,
            templateExamples.channelId,
        );

        titles.splice(getBaseKeyIndex(titles, t("ImportData.ChannelId")), 0, t("ImportData.ArticleQuantity"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.ChannelId")), 0, templateExamples.articleQuantity);

        titles.splice(getBaseKeyIndex(titles, t("ImportData.ArticleQuantity")), 0, t("ImportData.TitlePrice"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.ArticleQuantity")), 0, templateExamples.articlePrice);

        titles.splice(getBaseKeyIndex(titles, t("ImportData.TitlePrice")), 0, t("ImportData.GrossDuty"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.TitlePrice")), 0, templateExamples.grossDuty);
    } else if (conditions.usesFolio) {
        titles.splice(
            getBaseKeyIndex(titles, typeData.articleBeneficiaryWorkNature.label),
            0,
            t("ImportData.ArticleQuantity"),
        );
        examples.splice(
            getBaseKeyIndex(titles, typeData.articleBeneficiaryWorkNature.label),
            0,
            templateExamples.articleQuantity,
        );

        titles.splice(getBaseKeyIndex(titles, t("ImportData.ArticleQuantity")), 0, t("ImportData.TitlePrice"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.ArticleQuantity")), 0, templateExamples.articlePrice);
    } else {
        titles.splice(
            getBaseKeyIndex(titles, typeData.articleBeneficiaryWorkNature.label),
            0,
            t("ImportData.GrossDuty"),
        );
        examples.splice(
            getBaseKeyIndex(titles, typeData.articleBeneficiaryWorkNature.label),
            0,
            templateExamples.grossDuty,
        );
    }
    return [[...titles], [...examples]];
};

const addBeneficiaryExamples = (typeData, titles, examples, conditions, customFields) => {
    if (conditions.usesDirectPrecount) {
        titles.splice(getBaseKeyIndex(titles, typeData.socialSecurityNumber.label), 0, t("ImportData.BenPrecount"));
        examples.splice(getBaseKeyIndex(titles, typeData.socialSecurityNumber.label), 0, templateExamples.precount);
    } else if (conditions.urssaf !== false) {
        titles.splice(
            getBaseKeyIndex(titles, typeData.socialSecurityNumber.label),
            0,
            t("ImportData.BenSocialProfileType"),
        );
        examples.splice(
            getBaseKeyIndex(titles, typeData.socialSecurityNumber.label),
            0,
            templateExamples.socialProfileType,
        );
    }

    if (customFields.length > 0) {
        customFields.forEach((field) => {
            titles = [...titles, field.name];
            examples = [...examples, getCustomFieldExample(field.valueType)];
        });
    }
    return [titles, examples];
};

const addSalesExamples = (typeData, titles, examples, conditions) => {
    if (conditions.usesCountryMarket) {
        titles = [...titles, t("Management.CountrySaleFormat")];
        examples = [...examples, templateExamples.countrySale];
    }

    if (conditions.usesOverridenPriceSales) {
        titles = [
            ...titles,
            t("Management.ImportSpecificSalesPrice"),
            t("Management.ImportSpecificSalesPriceCurrency"),
        ];
        examples = [...examples, templateExamples.overridenPrice, templateExamples.priceCurrency];
    }
    if (conditions.choseAllChannels) {
        titles = [...titles, t("Channels.Id")];
        examples = [...examples, templateExamples.channelId];
    }
    if (conditions.customTurnoverLabels?.turnover_1) {
        titles = [...titles, conditions.customTurnoverLabels.turnover_1];
        examples = [...examples, templateExamples.turnover];
    }
    if (conditions.customTurnoverLabels?.turnover_2) {
        titles = [...titles, conditions.customTurnoverLabels.turnover_2];
        examples = [...examples, templateExamples.turnover];
    }
    if (conditions.usesDistributorSalesData) {
        titles = [...titles, t("Management.Rate"), t("Management.DistributorGrossPrice")];
        examples = [...examples, templateExamples.rate, templateExamples.distributorGrossPrice];
    }
    if (conditions.isCollectiveManagementChannel) {
        titles = [...titles, t("ImportData.CollectiveManagementKind")];
        examples = [...examples, templateExamples.collectiveManagementKind];
    }
    return [[...titles], [...examples]];
};

const addTermsExamples = (typeData, titles, examples, conditions) => {
    if (conditions.usesAdvanceCompensation) {
        titles.splice(getBaseKeyIndex(titles, typeData.compensate.label), 0, t("ImportData.CompensationGroup"));
        examples.splice(getBaseKeyIndex(titles, typeData.compensate.label), 0, templateExamples.compensationGroup);
    }

    if (conditions.usesInitialAdvance) {
        titles.splice(getBaseKeyIndex(titles, typeData.reference.label), 0, t("ImportData.InitialAdvance"));
        examples.splice(getBaseKeyIndex(titles, typeData.reference.label), 0, templateExamples.initialAdvance);

        titles.splice(getBaseKeyIndex(titles, t("ImportData.InitialAdvance")), 0, t("ImportData.CumulativeRights"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.InitialAdvance")), 0, templateExamples.cumulativeRights);
    }

    return [[...titles], [...examples]];
};

const addArticleExamples = (typeData, titles, examples, conditions) => {
    if (conditions.pressUsesBothChannels) {
        titles.splice(getBaseKeyIndex(titles, typeData.workNature.label), 0, t("ImportData.ChannelId"));
        examples.splice(getBaseKeyIndex(titles, typeData.workNature.label), 0, templateExamples.channelId);

        titles.splice(getBaseKeyIndex(titles, t("ImportData.ChannelId")), 0, t("ImportData.ArticleQuantity"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.ChannelId")), 0, templateExamples.articleQuantity);

        titles.splice(getBaseKeyIndex(titles, t("ImportData.ArticleQuantity")), 0, t("ImportData.TitlePrice"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.ArticleQuantity")), 0, templateExamples.articlePrice);

        titles.splice(getBaseKeyIndex(titles, t("ImportData.TitlePrice")), 0, t("ImportData.GrossDuty"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.TitlePrice")), 0, templateExamples.grossDuty);
    } else if (conditions.usesFolio) {
        titles.splice(getBaseKeyIndex(titles, typeData.workNature.label), 0, t("ImportData.ArticleQuantity"));
        examples.splice(getBaseKeyIndex(titles, typeData.workNature.label), 0, templateExamples.articleQuantity);

        titles.splice(getBaseKeyIndex(titles, t("ImportData.ArticleQuantity")), 0, t("ImportData.TitlePrice"));
        examples.splice(getBaseKeyIndex(titles, t("ImportData.ArticleQuantity")), 0, templateExamples.articlePrice);
    } else {
        titles.splice(getBaseKeyIndex(titles, typeData.workNature.label), 0, t("ImportData.GrossDuty"));
        examples.splice(getBaseKeyIndex(titles, typeData.workNature.label), 0, templateExamples.grossDuty);
    }
    return [[...titles], [...examples]];
};

const addProvisionExamples = (typeData, titles, examples, conditions) => {
    if (conditions.isProvisionAddedOnTitle) {
        titles.unshift(t("ImportData.TitleEAN13"));
        examples.unshift(templateExamples.ean13);
    } else {
        titles.unshift(t("ImportData.TitleTermId"));
        examples.unshift(templateExamples.termId);
    }
    return [[...titles], [...examples]];
};

export const getCustomFieldExample = (
    type: "string" | "number" | "longstring" | "date" | "fileimport" | "link" | "boolean",
) => {
    switch (type) {
        case "number":
            return 20;
        case "longstring":
            return t("ImportData.LongTextExample");
        case "date":
            return "2025-05-01";
        case "boolean":
            return t("ImportData.BooleanExample");
        default:
            return t("ImportData.TextExample");
    }
};

/**
 * Allows us to insert a conditional column at a specific place within the object.
 * @returns Updated object
 */
const insertPairInObject = (object: object, insertAfter: string, newPair: { key: string; value: any }) => {
    const newObject = {};
    for (const key of Object.keys(object)) {
        newObject[key] = object[key];
        if (key === insertAfter) {
            newObject[newPair.key] = newPair.value;
        }
    }

    return newObject;
};
